import React, { useEffect, useState } from "react";
import NewsMetaHelmet from "./NewsMetaHelmet";
import { useParams, Link } from "react-router-dom";
import { getNewsShareUrl } from "../utils/share";
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Modal,
    Dropdown,
    ButtonGroup,
} from "react-bootstrap";

const BASE = (() => {
    let url = process.env.REACT_APP_API_URL || "https://news-portal-l6pf.onrender.com";
    url = url.replace(/\/$/, "");
    if (url.endsWith("/api")) url = url.slice(0, -4);
    return url;
})();

const API_URL = `${BASE}/api/news`;

const formatCategoryName = (value) => {
    if (!value) return "";
    return value
        .split(" ")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
};

const getImageUrl = (item) => {
    const placeholder = "https://placehold.co/1200x650?text=No+Image";
    if (!item) return placeholder;

    const resolveUrl = (path) => {
        if (!path || typeof path !== "string" || path.trim() === "") return null;
        const p = path.trim();

        if (p.startsWith("http://") || p.startsWith("https://")) return p;
        if (p.startsWith("/uploads/")) return `${BASE}${p}`;
        if (p.startsWith("uploads/")) return `${BASE}/${p}`;
        if (p.includes(".")) return `${BASE}/uploads/${p}`;

        return null;
    };

    return (
        resolveUrl(item.imagePath) ||
        resolveUrl(item.image) ||
        resolveUrl(item.imageUrl) ||
        resolveUrl(item.imageURL) ||
        resolveUrl(item.thumbnail) ||
        placeholder
    );
};

const getDescription = (item) => {
    return (
        item.shortDesc ||
        item.description ||
        item.summary ||
        item.content ||
        "No description available."
    );
};

const getLongDescription = (item) => {
    return (
        item.content ||
        item.description ||
        item.summary ||
        item.shortDesc ||
        "No description available."
    );
};

const getViews = (item) => {
    return Number(item.views ?? item.totalViews ?? item.viewCount ?? 0);
};

const updateLocalViews = (items, id) => {
    return items.map((item) => {
        if (item._id === id || item.id === id) {
            return { ...item, views: getViews(item) + 1 };
        }
        return item;
    });
};

const openNewTab = (url) => window.open(url, "_blank", "noopener,noreferrer");

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        alert("Link copied!");
    } catch {
        window.prompt("Copy this link:", text);
    }
};

const shareTo = (platform, item) => {
    if (!item) return;
    const url = getNewsShareUrl(item);
    const title = (item.title || "News article").trim();
    const text = `${title}\n${url}`;
    switch (platform) {
        case "whatsapp":
            openNewTab(`https://wa.me/?text=${encodeURIComponent(text)}`);
            break;
        case "facebook":
            openNewTab(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
            break;
        case "x":
            openNewTab(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`);
            break;
        case "telegram":
            openNewTab(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`);
            break;
        case "linkedin":
            openNewTab(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`);
            break;
        case "email":
            openNewTab(`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text)}`);
            break;
        case "copy":
            copyToClipboard(url);
            break;
        default:
            break;
    }
};

const ShareMenu = ({ item, size = "sm" }) => (
    <Dropdown as={ButtonGroup} className="share-group">
        <Button size={size} variant="success" className="share-btn" onClick={() => shareTo("whatsapp", item)}>
            <i className="bi bi-share-fill" /> Share
        </Button>
        <Dropdown.Toggle split size={size} variant="success" />
        <Dropdown.Menu align="end" className="share-dropdown">
            <Dropdown.Item className="share-item whatsapp" onClick={() => shareTo("whatsapp", item)}>
                <i className="bi bi-whatsapp" /> WhatsApp
            </Dropdown.Item>
            <Dropdown.Item className="share-item facebook" onClick={() => shareTo("facebook", item)}>
                <i className="bi bi-facebook" /> Facebook
            </Dropdown.Item>
            <Dropdown.Item className="share-item x" onClick={() => shareTo("x", item)}>
                <i className="bi bi-twitter-x" /> X (Twitter)
            </Dropdown.Item>
            <Dropdown.Item className="share-item telegram" onClick={() => shareTo("telegram", item)}>
                <i className="bi bi-telegram" /> Telegram
            </Dropdown.Item>
            <Dropdown.Item className="share-item linkedin" onClick={() => shareTo("linkedin", item)}>
                <i className="bi bi-linkedin" /> LinkedIn
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item className="share-item email" onClick={() => shareTo("email", item)}>
                <i className="bi bi-envelope-fill" /> Email
            </Dropdown.Item>
            <Dropdown.Item className="share-item copy" onClick={() => shareTo("copy", item)}>
                <i className="bi bi-link-45deg" /> Copy Link
            </Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
);

const incrementNewsViews = async (id) => {
    try {
        await fetch(`${API_URL}/views/${id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
        });
    } catch (err) {
        console.error("Unable to update news views:", err);
    }
};

const CategoryPage = ({ fixedCategory, pageTitle }) => {
    const { category } = useParams();
    const [newsData, setNewsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectedNews, setSelectedNews] = useState(null);

    const categoryValue = fixedCategory || category || "";
    const normalizedCategory = decodeURIComponent(categoryValue).replace(/%20/g, " ");
    const displayCategory = formatCategoryName(normalizedCategory.replace(/-/g, " "));

    useEffect(() => {
        const fetchCategoryNews = async () => {
            if (!normalizedCategory) {
                setNewsData([]);
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");
            try {
                const response = await fetch(`${API_URL}?category=${encodeURIComponent(displayCategory)}&limit=100`);
                const result = await response.json();
                const articles = Array.isArray(result?.data) ? result.data : [];
                setNewsData(articles);
            } catch (err) {
                setError("Unable to load category news. Please try again.");
                console.error("CategoryPage Fetch Error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchCategoryNews();
    }, [displayCategory, normalizedCategory]);

    return (
        <Container className="py-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h2 className="mb-1">{pageTitle || `Category: ${displayCategory || "Unknown"}`}</h2>
                    <p className="text-muted">Showing news articles for this category.</p>
                </div>
                <Link to="/" className="btn btn-outline-secondary">
                    Back to Home
                </Link>
            </div>

            {loading ? null : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : newsData.length === 0 ? (
                <div className="alert alert-info">
                    No published news found for "{displayCategory}".
                </div>
            ) : (
                <Row xs={1} md={2} lg={3} className="g-4">
                    {newsData.map((item) => {
                        const description = getDescription(item);
                        const preview = description.length > 140 ? `${description.slice(0, 140).trim()}...` : description;
                        const views = getViews(item);
                        return (
                            <Col key={item._id || item.id}>
                                <Card className="h-100 shadow-sm">
                                    <div className="ratio ratio-16x9 overflow-hidden">
                                        <Card.Img
                                            variant="top"
                                            src={getImageUrl(item)}
                                            alt={item.title || "Thumbnail"}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://placehold.co/1200x650?text=Image+Not+Found";
                                            }}
                                            className="object-fit-cover"
                                        />
                                    </div>
                                    <Card.Body className="d-flex flex-column">
                                        <Badge bg="secondary" className="mb-2 text-uppercase">
                                            {item.category || displayCategory}
                                        </Badge>
                                        <Card.Title>{item.title || "Untitled"}</Card.Title>
                                        <div className="mb-3 d-flex align-items-center text-muted">
                                            <span className="me-2" role="img" aria-label="views">👁️</span>
                                            <small>{views} views</small>
                                        </div>
                                        <Card.Text className="mb-3 news-description">
                                            {preview}
                                        </Card.Text>
                                        <div className="mb-3 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                            <small className="text-muted">{new Date(item.createdAt).toLocaleDateString()}</small>
                                            <ShareMenu item={item} size="sm" />
                                        </div>
                                        <div className="d-flex justify-content-end">
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={async () => {
                                                    const id = item._id || item.id;
                                                    setNewsData((prev) => updateLocalViews(prev, id));
                                                    setSelectedNews({ ...item, views: views + 1 });
                                                    await incrementNewsViews(id);
                                                }}
                                            >
                                                Read More
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

                        <Modal
                                show={Boolean(selectedNews)}
                                onHide={() => setSelectedNews(null)}
                                size="lg"
                                centered
                        >
                                {selectedNews && (
                                    <NewsMetaHelmet
                                        title={selectedNews.title || "News Article"}
                                        description={getLongDescription(selectedNews)}
                                        image={getImageUrl(selectedNews)}
                                        url={window.location.href}
                                    />
                                )}
                <Modal.Header closeButton>
                    <Modal.Title>{selectedNews?.title || "Article details"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedNews && (
                        <>
                            <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
                                <Badge bg="secondary" className="text-uppercase">
                                    {selectedNews.category || displayCategory}
                                </Badge>
                                <small className="text-muted d-flex align-items-center">
                                    <span className="me-1" role="img" aria-label="views">👁️</span>
                                    {selectedNews.views} views
                                </small>
                                <small className="text-muted">{new Date(selectedNews.createdAt).toLocaleDateString()}</small>
                            </div>
                            <div className="ratio ratio-16x9 overflow-hidden mb-3">
                                <img
                                    src={getImageUrl(selectedNews)}
                                    alt={selectedNews.title || "Article image"}
                                    className="object-fit-cover w-100 h-100"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "https://placehold.co/1200x650?text=Image+Not+Found";
                                    }}
                                />
                            </div>
                            <h6>Full Description</h6>
                            <p className="news-article-text">{getLongDescription(selectedNews)}</p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectedNews(null)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default CategoryPage;
